import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Users, Check, X, Trash2, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Clickable Reservation Card (in sidebar)
const ClickableReservation = ({ reservation, isSelected, onClick, onUnassign }) => {
  const hasTable = reservation.table_ids && reservation.table_ids.length > 0;

  return (
    <div
      onClick={() => onClick(reservation)}
      className={`p-3 rounded-lg border transition-all cursor-pointer ${
        isSelected
          ? 'bg-brand-gold/20 border-brand-gold ring-2 ring-brand-gold'
          : hasTable 
            ? 'bg-green-500/10 border-green-500/30 hover:border-green-500/50' 
            : 'bg-[#222222] border-white/10 hover:border-brand-gold/50'
      }`}
    >
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-brand-gold">{reservation.time}</span>
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Users className="w-3 h-3" />
              {reservation.party_size}
            </span>
          </div>
          <p className="text-sm text-gray-300 truncate">{reservation.guest_name || reservation.guest?.name || 'Onbekend'}</p>
          {hasTable && !isSelected && (
            <p className="text-xs text-green-400 mt-1">
              ✓ {reservation.tables?.map(t => `${t.type === 'bar' ? 'B' : 'T'}${t.number}`).join(' + ')}
            </p>
          )}
        </div>
        {hasTable && !isSelected && (
          <button
            onClick={(e) => { e.stopPropagation(); onUnassign(reservation.id); }}
            className="p-1 rounded bg-red-500/20 hover:bg-red-500/40 text-red-400"
            title="Toewijzing verwijderen"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      {isSelected && (
        <p className="text-xs text-brand-gold mt-2 pt-2 border-t border-brand-gold/30">
          → Klik nu op tafels om toe te wijzen
        </p>
      )}
    </div>
  );
};

// Clickable Table on the plan
const ClickableTable = ({ table, tableReservations, onTableClick, isSelected, hasTimeConflict, selectedReservation }) => {
  const reservationCount = tableReservations.length;
  const hasMultiple = reservationCount > 1;
  
  const getStatusColor = () => {
    if (isSelected) return "#D4AF37"; // Gold when selected for current reservation
    if (hasTimeConflict) return "#EF4444"; // Red when there's a time conflict
    if (hasMultiple) return "#F59E0B"; // Orange for multiple reservations
    if (reservationCount === 1) return "#6366F1"; // Purple when occupied by one reservation
    return "#374151"; // Gray when free
  };

  const isBarSeat = table.type === 'bar';
  const isDecor = table.type === 'decor';
  
  if (isDecor) {
    return (
      <div
        className="absolute rounded border-2 border-dashed border-gray-700 flex items-center justify-center"
        style={{
          left: `${table.x}px`,
          top: `${table.y}px`,
          transform: 'translate(-50%, -50%)',
          width: table.width || '120px',
          height: table.height || '60px',
          backgroundColor: '#2A2520'
        }}
      >
        <span className="text-xs text-gray-500">{table.name}</span>
      </div>
    );
  }

  const shapeClass = table.shape === 'circular' ? 'rounded-full' : 
                     table.shape === 'square' ? 'rounded-md' : 'rounded-lg';
  
  const width = isBarSeat ? '40px' : 
                table.shape === 'circular' ? '80px' : 
                table.shape === 'square' ? '60px' : '100px';
  const height = isBarSeat ? '40px' : 
                 table.shape === 'circular' ? '80px' : 
                 table.shape === 'square' ? '60px' : '65px';

  const prefix = isBarSeat ? 'B' : 'T';

  // Build tooltip
  let tooltipText = `${prefix}${table.number}: `;
  if (reservationCount === 0) {
    tooltipText += 'Vrij';
  } else {
    tooltipText += tableReservations.map(r => 
      `${r.time} - ${r.guest_name || r.guest?.name || 'Gereserveerd'}`
    ).join(' | ');
  }

  return (
    <div
      onClick={() => onTableClick(table)}
      className={`absolute ${shapeClass} flex flex-col items-center justify-center transition-all cursor-pointer hover:scale-105 ${
        isSelected ? 'scale-110 shadow-lg ring-2 ring-brand-gold' : ''
      } ${hasTimeConflict ? 'opacity-50 cursor-not-allowed' : ''}`}
      style={{
        left: `${table.x}px`,
        top: `${table.y}px`,
        transform: 'translate(-50%, -50%)',
        width,
        height,
        backgroundColor: isSelected ? '#3D3520' : hasMultiple ? '#F59E0B15' : '#1A1A1A',
        border: `3px solid ${getStatusColor()}`,
        zIndex: isSelected ? 10 : 1
      }}
      title={tooltipText}
    >
      <span className="text-sm font-bold text-white">{prefix}{table.number}</span>
      <span className="text-[10px] text-gray-400">{table.capacity}p</span>
      {reservationCount === 1 && !isSelected && (
        <span className="text-[8px] text-purple-300 truncate max-w-full px-1">
          {(tableReservations[0].guest_name || tableReservations[0].guest?.name || '').split(' ')[0]}
        </span>
      )}
      {isSelected && (
        <Check className="w-3 h-3 text-brand-gold absolute -top-1 -right-1" />
      )}
      {/* Show reservation count badge for multiple reservations */}
      {hasMultiple && !isBarSeat && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center text-[9px] font-bold text-black">
          {reservationCount}
        </span>
      )}
    </div>
  );
};

const TableAssignment = () => {
  const { t } = useTranslation();
  const [tables, setTables] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [selectedTableIds, setSelectedTableIds] = useState([]);

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  // Reset selection when date changes
  useEffect(() => {
    setSelectedReservation(null);
    setSelectedTableIds([]);
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Use local date formatting to avoid timezone issues
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const [tablesRes, reservationsRes] = await Promise.all([
        axios.get(`${API}/tables`),
        axios.get(`${API}/reservations?date=${dateStr}`)
      ]);
      
      setTables(tablesRes.data);
      
      // Fetch guest details and table info for each reservation
      const reservationsWithDetails = await Promise.all(
        reservationsRes.data.map(async (reservation) => {
          try {
            const guestRes = await axios.get(`${API}/guests/${reservation.guest_id}`);
            const tableIds = reservation.table_ids || (reservation.table_id ? [reservation.table_id] : []);
            const assignedTables = tableIds.map(tid => tablesRes.data.find(t => t.id === tid)).filter(Boolean);
            
            return {
              ...reservation,
              guest: guestRes.data,
              tables: assignedTables
            };
          } catch (e) {
            return { ...reservation, guest: { name: 'Unknown' }, tables: [] };
          }
        })
      );
      
      setReservations(reservationsWithDetails);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Fout bij laden");
    } finally {
      setLoading(false);
    }
  };

  const getReservationForTable = (tableId) => {
    return reservations.find(r => 
      r.table_id === tableId || 
      (r.table_ids && r.table_ids.includes(tableId))
    );
  };

  // Get ALL reservations for a table (for multiple shifts)
  const getAllReservationsForTable = (tableId) => {
    return reservations.filter(r => 
      r.table_id === tableId || 
      (r.table_ids && r.table_ids.includes(tableId))
    );
  };

  // Convert time string (HH:MM) to minutes since midnight
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Check if two reservations have a time conflict (less than 150 minutes apart)
  const hasTimeConflict = (reservation1Time, reservation2Time) => {
    const time1 = timeToMinutes(reservation1Time);
    const time2 = timeToMinutes(reservation2Time);
    const diff = Math.abs(time1 - time2);
    return diff < 150; // 2u30 = 150 minutes
  };

  // Check if a table has a time conflict with the selected reservation
  const tableHasTimeConflict = (tableId) => {
    if (!selectedReservation) return false;
    
    const tableReservations = getAllReservationsForTable(tableId);
    
    // Check if any existing reservation conflicts with the selected one
    return tableReservations.some(r => {
      // Skip if it's the same reservation
      if (r.id === selectedReservation.id) return false;
      return hasTimeConflict(r.time, selectedReservation.time);
    });
  };

  const findTablesForPartySize = (startTable, partySize) => {
    // Get available tables (not decor, not already assigned)
    const availableTables = tables.filter(t => {
      if (t.type === 'decor') return false;
      const hasReservation = reservations.some(r => 
        r.table_ids?.includes(t.id) || r.table_id === t.id
      );
      return !hasReservation || t.id === startTable.id;
    });

    // If the start table can fit the party, just use it
    if (startTable.capacity >= partySize) {
      return [startTable];
    }

    // Find nearby tables to merge
    const sortedByDistance = availableTables
      .filter(t => t.id !== startTable.id)
      .map(t => ({
        table: t,
        distance: Math.sqrt(Math.pow(t.x - startTable.x, 2) + Math.pow(t.y - startTable.y, 2))
      }))
      .sort((a, b) => a.distance - b.distance);

    let totalCapacity = startTable.capacity;
    const selectedTables = [startTable];

    for (const { table } of sortedByDistance) {
      if (totalCapacity >= partySize) break;
      selectedTables.push(table);
      totalCapacity += table.capacity;
    }

    return totalCapacity >= partySize ? selectedTables : null;
  };

  const handleDragStart = (event) => {};
  const handleDragOver = (event) => {};
  const handleDragEnd = async (event) => {};
  const handleDragCancel = () => {};

  const unassignReservation = async (reservationId) => {
    try {
      await axios.delete(`${API}/reservations/${reservationId}/unassign-table`);
      toast.success("Tafel toewijzing verwijderd");
      setSelectedReservation(null);
      setSelectedTableIds([]);
      fetchData();
    } catch (error) {
      console.error("Error unassigning:", error);
      toast.error("Fout bij verwijderen toewijzing");
    }
  };

  // Unassign ALL reservations
  const unassignAllReservations = async () => {
    const assignedRes = reservations.filter(r => r.table_ids && r.table_ids.length > 0);
    if (assignedRes.length === 0) {
      toast.info("Geen toewijzingen om te verwijderen");
      return;
    }
    
    if (!window.confirm(`Weet je zeker dat je alle ${assignedRes.length} tafeltoewijzingen wilt verwijderen?`)) {
      return;
    }

    try {
      await Promise.all(
        assignedRes.map(res => 
          axios.delete(`${API}/reservations/${res.id}/unassign-table`)
        )
      );
      toast.success(`${assignedRes.length} toewijzingen verwijderd`);
      setSelectedReservation(null);
      setSelectedTableIds([]);
      fetchData();
    } catch (error) {
      console.error("Error unassigning all:", error);
      toast.error("Fout bij verwijderen toewijzingen");
    }
  };

  // Handle clicking on a reservation - select it for table assignment
  const handleReservationClick = (reservation) => {
    if (selectedReservation?.id === reservation.id) {
      // Deselect if clicking the same reservation
      setSelectedReservation(null);
      setSelectedTableIds([]);
    } else {
      // Select new reservation and load its current tables
      setSelectedReservation(reservation);
      setSelectedTableIds(reservation.table_ids || []);
    }
  };

  // Handle clicking on a table - toggle it for selected reservation
  const handleTableClick = (table) => {
    if (table.type === 'decor') return;
    
    // If no reservation selected, show a hint
    if (!selectedReservation) {
      toast.info("Selecteer eerst een reservering aan de linkerkant");
      return;
    }
    
    // Check if table has a TIME conflict with the selected reservation
    if (tableHasTimeConflict(table.id)) {
      const conflictingRes = getAllReservationsForTable(table.id).find(r => 
        r.id !== selectedReservation.id && hasTimeConflict(r.time, selectedReservation.time)
      );
      const conflictName = conflictingRes?.guest_name || conflictingRes?.guest?.name || 'een andere reservering';
      toast.error(`Tafel ${table.type === 'bar' ? 'B' : 'T'}${table.number} heeft een tijdsconflict met ${conflictName} (${conflictingRes?.time}). Minimum 2u30 tussen reserveringen.`);
      return;
    }
    
    // Toggle the table selection
    setSelectedTableIds(prev => {
      if (prev.includes(table.id)) {
        return prev.filter(id => id !== table.id);
      } else {
        return [...prev, table.id];
      }
    });
  };

  // Save the table assignment
  const saveTableAssignment = async () => {
    if (!selectedReservation) return;
    
    if (selectedTableIds.length === 0) {
      toast.error("Selecteer minstens één tafel");
      return;
    }
    
    // Check capacity
    const totalCapacity = selectedTableIds.reduce((sum, tid) => {
      const table = tables.find(t => t.id === tid);
      return sum + (table?.capacity || 0);
    }, 0);
    
    if (totalCapacity < selectedReservation.party_size) {
      toast.error(`Capaciteit (${totalCapacity}p) is onvoldoende voor ${selectedReservation.party_size} personen`);
      return;
    }

    try {
      const tableIds = selectedTableIds.join(',');
      await axios.put(`${API}/reservations/${selectedReservation.id}/assign-table?table_ids=${tableIds}`);
      
      const tableNumbers = selectedTableIds.map(tid => {
        const t = tables.find(table => table.id === tid);
        return t ? `${t.type === 'bar' ? 'B' : 'T'}${t.number}` : '';
      }).filter(Boolean).join(' + ');
      
      toast.success(`${selectedReservation.guest_name || selectedReservation.guest?.name || 'Gast'} toegewezen aan ${tableNumbers}`);
      
      setSelectedReservation(null);
      setSelectedTableIds([]);
      fetchData();
    } catch (error) {
      console.error("Error assigning tables:", error);
      toast.error("Fout bij toewijzen tafel");
    }
  };

  // Cancel selection
  const cancelSelection = () => {
    setSelectedReservation(null);
    setSelectedTableIds([]);
  };

  // Check if a table is occupied by another reservation (not the selected one)
  const isTableOccupiedByOther = (tableId) => {
    const res = getReservationForTable(tableId);
    return res && (!selectedReservation || res.id !== selectedReservation.id);
  };

  const unassignedReservations = reservations.filter(r => !r.table_ids || r.table_ids.length === 0);
  const assignedReservations = reservations.filter(r => r.table_ids && r.table_ids.length > 0);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-300">Laden...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="table-assignment-view">
      <div className="p-6 border-b border-white/10 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif text-gray-300">Tafel Toewijzing</h2>
          <p className="text-sm text-gray-400">Klik op een reservering, dan op tafels om toe te wijzen</p>
        </div>
        <Button
          onClick={unassignAllReservations}
          variant="outline"
          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Alle toewijzingen wissen
        </Button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar with reservations */}
        <div className="w-80 border-r border-white/10 bg-[#121212] flex flex-col">
          <div className="p-4 border-b border-white/10">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                if (date) {
                  const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
                  setSelectedDate(localDate);
                }
              }}
              className="rounded-md border border-white/20"
            />
          </div>
          
          <div className="flex-1 overflow-auto p-4 space-y-4">
            {/* Unassigned Reservations */}
            <div>
              <h3 className="text-sm font-semibold text-gray-400 mb-2 flex items-center gap-2">
                <X className="w-4 h-4 text-red-400" />
                Zonder Tafel ({unassignedReservations.length})
              </h3>
              <div className="space-y-2">
                {unassignedReservations.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-2">Alle reserveringen hebben een tafel</p>
                ) : (
                  unassignedReservations.map(res => (
                    <ClickableReservation 
                      key={res.id} 
                      reservation={res} 
                      isSelected={selectedReservation?.id === res.id}
                      onClick={handleReservationClick}
                      onUnassign={unassignReservation}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Assigned Reservations */}
            <div>
              <h3 className="text-sm font-semibold text-gray-400 mb-2 flex items-center gap-2">
                <Check className="w-4 h-4 text-green-400" />
                Met Tafel ({assignedReservations.length})
              </h3>
              <div className="space-y-2">
                {assignedReservations.map(res => (
                  <ClickableReservation 
                    key={res.id} 
                    reservation={res} 
                    isSelected={selectedReservation?.id === res.id}
                    onClick={handleReservationClick}
                    onUnassign={unassignReservation}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Table Plan */}
        <div className="flex-1 overflow-auto p-6 bg-[#0A0A0A]">
          {/* Selection Bar */}
          {selectedReservation && (
            <div className="mb-4 p-4 bg-brand-gold/10 border border-brand-gold/30 rounded-lg flex items-center justify-between">
              <div>
                <p className="text-brand-gold font-semibold">
                  {selectedReservation.time} - {selectedReservation.guest_name || selectedReservation.guest?.name}
                </p>
                <p className="text-sm text-gray-400">
                  {selectedReservation.party_size} personen • Klik op tafels om toe te wijzen
                  {selectedTableIds.length > 0 && (
                    <span className="ml-2 text-brand-gold">
                      (Geselecteerd: {selectedTableIds.map(tid => {
                        const t = tables.find(table => table.id === tid);
                        return t ? `${t.type === 'bar' ? 'B' : 'T'}${t.number}` : '';
                      }).filter(Boolean).join(', ')})
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={cancelSelection}
                  variant="outline"
                  className="border-gray-600"
                >
                  Annuleren
                </Button>
                <Button
                  onClick={saveTableAssignment}
                  className="bg-brand-gold text-black hover:bg-brand-gold/90"
                  disabled={selectedTableIds.length === 0}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Opslaan
                </Button>
              </div>
            </div>
          )}

          <div 
            className="relative w-full min-h-[600px] bg-[#1A1A1A] border border-white/10 rounded-lg"
            style={{
              backgroundImage: 'radial-gradient(circle, #333 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }}
          >
            {tables.map(table => (
              <ClickableTable
                key={table.id}
                table={table}
                tableReservations={getAllReservationsForTable(table.id)}
                onTableClick={handleTableClick}
                isSelected={selectedTableIds.includes(table.id)}
                hasTimeConflict={tableHasTimeConflict(table.id)}
                selectedReservation={selectedReservation}
              />
            ))}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-6 mt-4 text-sm flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-gray-600" />
              <span className="text-gray-400">Vrij</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-purple-500" />
              <span className="text-gray-400">1 reservering</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-amber-500" />
              <span className="text-gray-400">2+ reserveringen</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-brand-gold" />
              <span className="text-gray-400">Geselecteerd</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-red-500" />
              <span className="text-gray-400">Tijdsconflict (&lt;2u30)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableAssignment;
